/**
 * /tools/swagger - the published API specs, rendered inside the app with the signed-in user's context
 * already filled into the fields that take it.
 *
 * Two things drive the shape of this. Both are about the frame.
 *
 * The spec is rendered inside a frame rather than in the app's own document because swagger-ui's
 * stylesheet is more than half rem-based (863 rem declarations against 567 px ones), and VUD sets
 * `html { font-size: 62.5% }`. rem always resolves against `html`, which the navigation needs left
 * alone, so in the app's document every rem-sized rule would render at ~62.5% while the px-sized
 * ones stayed put. A frame gets its own 16px root and the spec renders as designed.
 *
 * The frame is built here rather than pointed at /apidocs because prefilling means handing
 * swagger-ui a patched spec, which means owning the call to SwaggerUIBundle. That also drops the
 * topbar - the part that shows the spec URL - since the topbar comes from the StandalonePreset, which
 * this deliberately does not load. The definition picker lived in that topbar, so the select below
 * replaces it: same list, read from the same place, without the URL on show.
 *
 * swagger-ui itself is not a dependency: its bundle and stylesheet are already served from /apidocs,
 * which is same-origin in stag, acc and prod - PUBLIC_URL is that same host - and is proxied to stag
 * in local dev by vite.config.ts.
 *
 * Gated behind the same `fe-api-tool` flag as /tools/api. The flag is read here rather than around
 * the <Route> for the same reason it is in the API tool: the router is built once, above
 * AppInitialization, while `launchDarkly.userClient` only exists after /startup has answered.
 */
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {launchDarkly} from "utils/launchDarkly";
import * as reduxSelectors from "components/store/application.reducers";
import {NotFound} from "components/errorpage/ErrorPage.component.jsx";
import {
    DEFAULT_SPEC_NAME,
    FALLBACK_SPECS,
    isPatchable,
    parseSpecList,
    SPEC_LIST_URL,
    withContextDefaults,
} from "./swaggerSpec.js";
import styles from "./swagger.module.css";

/*
 * The frame's whole document. It exposes a function for the app to call rather than listening for a
 * message: the frame is same-origin, so a direct call needs no origin handshake to get wrong - and
 * `location.origin` inside a srcdoc document is exactly the kind of thing that differs between
 * browsers.
 *
 * It takes either a patched spec object or a URL for swagger-ui to load itself, which is what lets
 * the YAML specs work without bundling a YAML parser.
 */
const FRAME_DOCUMENT = `<!DOCTYPE html>
<html lang="en">
<head>
<link rel="stylesheet" href="/apidocs/swagger-ui.css">
<style>body {margin: 0}</style>
</head>
<body>
<div id="swagger-ui"></div>
<script src="/apidocs/swagger-ui-bundle.js"></script>
<script>
window.render = function (source) {
    window.SwaggerUIBundle(Object.assign({}, source, {
        dom_id: "#swagger-ui",
        presets: [window.SwaggerUIBundle.presets.apis],
        /* the tag groups open, the operations inside them collapsed */
        docExpansion: "list",
        defaultModelsExpandDepth: 1,
        withCredentials: true
    }));
};
</script>
</body>
</html>`;

/* Named so the message can say which half is missing rather than leaving a blank frame. */
const PROXY_HINT = "swagger-ui could not be loaded from /apidocs. Locally that path is served by the"
    + " proxy in vite.config.ts, which only takes effect after the dev server restarts.";

export const Swagger = () => {
    const companyId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const userId = useSelector(reduxSelectors.getUsersId);

    const [specs, setSpecs] = useState(null);
    const [selectedUrl, setSelectedUrl] = useState(null);
    // what to hand the frame: {spec} for the JSON specs, {url} for the YAML ones
    const [source, setSource] = useState(null);
    const [error, setError] = useState(null);
    const [height, setHeight] = useState(null);
    // the frame and the spec arrive in either order, so both are tracked and the send waits for both
    const [frameLoaded, setFrameLoaded] = useState(false);
    const frameRef = useRef(null);

    const selected = useMemo(
        () => specs?.find(spec => spec.url === selectedUrl) ?? null,
        [specs, selectedUrl],
    );

    /*
     * The page's height is whatever the navigation above it leaves, which is not a fixed number, so
     * measure rather than hard-code. Setting the height does not move the element, so this cannot
     * loop.
     */
    useEffect(() => {
        const measure = () => {
            const top = frameRef.current?.getBoundingClientRect().top ?? 0;
            setHeight(`calc(100vh - ${Math.round(top)}px)`);
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    /*
     * Which specs exist comes from apidocs itself, so one added there appears here without a code
     * change. A list that cannot be read falls back to the bundled one rather than to an empty picker.
     */
    useEffect(() => {
        let cancelled = false;
        fetch(SPEC_LIST_URL, {credentials: "include"})
            .then(response => (response.ok ? response.text() : Promise.reject(new Error("not ok"))))
            .then(body => !cancelled && setSpecs(parseSpecList(body)))
            .catch(() => !cancelled && setSpecs(FALLBACK_SPECS));
        return () => {
            cancelled = true;
        };
    }, []);

    // open on the spec this page was built for, falling back to whatever is listed first
    useEffect(() => {
        if (specs != null && selectedUrl == null) {
            const preferred = specs.find(spec => spec.name === DEFAULT_SPEC_NAME) ?? specs[0];
            setSelectedUrl(preferred?.url ?? null);
        }
    }, [specs, selectedUrl]);

    // same-origin in every environment, so no CORS and no credentials to arrange
    useEffect(() => {
        if (selected == null) {
            return undefined;
        }
        setError(null);
        setSource(null);

        // swagger-ui loads and parses this one itself
        if (!isPatchable(selected)) {
            setSource({url: selected.url});
            return undefined;
        }

        let cancelled = false;
        fetch(selected.url, {credentials: "include"})
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Could not load ${selected.name}: GET ${selected.url} → ${response.status} ${response.statusText}`);
                }
                // an unproxied path answers with index.html and a 200, so a parse failure means the proxy
                return response.json().catch(() => {
                    throw new Error(PROXY_HINT);
                });
            })
            .then(body => !cancelled && setSource({spec: body}))
            .catch(caught => !cancelled && setError(caught.message));
        return () => {
            cancelled = true;
        };
    }, [selected]);

    // re-renders if the context changes, so switching company refills the fields with the new id
    useEffect(() => {
        if (source == null || !frameLoaded) {
            return;
        }
        /*
         * If /apidocs did not serve the bundle, the frame's script tag loaded something that is not
         * swagger-ui - the dev server answers an unproxied path with index.html - and neither of
         * these exists. Saying so beats leaving an empty frame and no reason for it.
         */
        const frameWindow = frameRef.current?.contentWindow;
        if (typeof frameWindow?.render !== "function" || frameWindow.SwaggerUIBundle == null) {
            setError(PROXY_HINT);
            return;
        }
        frameWindow.render(source.spec != null
            ? {spec: withContextDefaults(source.spec, {companyId, userId})}
            : {url: source.url});
    }, [source, frameLoaded, companyId, userId]);

    const loading = error == null && source == null;

    return (
        <div className={styles.root} style={height ? {height} : undefined}>
            <div className={styles.bar}>
                <label className={styles.picker}>
                    API{" "}
                    <select
                        value={selectedUrl ?? ""}
                        disabled={specs == null}
                        onChange={event => setSelectedUrl(event.target.value)}
                    >
                        {specs == null
                            ? <option value="">loading…</option>
                            : specs.map(spec => (
                                <option key={spec.url} value={spec.url}>{spec.name}</option>
                            ))}
                    </select>
                </label>
                {loading && <span className={styles.muted}>Loading…</span>}
                {error != null && <span className={styles.error}>{error}</span>}
            </div>
            <iframe
                ref={frameRef}
                srcDoc={FRAME_DOCUMENT}
                title="API reference"
                className={`${styles.frame} ${source == null ? styles.pending : ""}`}
                onLoad={() => setFrameLoaded(true)}
            />
        </div>
    );
};

export default function SwaggerPage() {
    const enabled = launchDarkly.userClient?.variation("fe-api-tool", false);
    return enabled ? <Swagger/> : <NotFound/>;
}
